/**
 * Plot scene builder: turns a declarative plot spec into drawing primitives
 * in pixel coordinates. The same scene is rendered to canvas (interactive)
 * and to SVG (vector export), so exports match the screen exactly.
 */
import { fmt } from '../core/units';

export interface Series {
  id: string;
  label: string;
  x: ArrayLike<number>;
  y: ArrayLike<number>;
  color: string;
  width?: number;
  dash?: number[];
  axis?: 'y' | 'y2';
  /** Draw sample points as dots (used for sparse data). */
  dots?: boolean;
}

export interface Band {
  x: ArrayLike<number>;
  lo: ArrayLike<number>;
  hi: ArrayLike<number>;
  color: string;
}

export interface VLine {
  x: number;
  color: string;
  dash?: number[];
  label?: string;
}

export interface HLine {
  y: number;
  color: string;
  dash?: number[];
  label?: string;
}

export interface Span {
  x0: number;
  x1: number;
  color: string;
}

export interface WidthMark {
  x0: number;
  x1: number;
  y: number;
  color: string;
  label?: string;
}

export interface PointMark {
  x: number;
  y: number;
  color: string;
  label?: string;
}

export interface PlotSpec {
  series: Series[];
  bands?: Band[];
  vlines?: VLine[];
  hlines?: HLine[];
  spans?: Span[];
  widths?: WidthMark[];
  points?: PointMark[];
  xLabel: string;
  yLabel: string;
  y2Label?: string;
  xRange?: [number, number] | null;
  yRange?: [number, number] | null;
  yLog?: boolean;
  title?: string;
  legend?: boolean;
  theme?: PlotTheme;
}

export interface PlotTheme {
  bg: string;
  grid: string;
  axis: string;
  text: string;
  muted: string;
  font: string;
  mono: string;
}

export const DARK_THEME: PlotTheme = {
  bg: '#15171b',
  grid: 'rgba(255,255,255,0.07)',
  axis: 'rgba(255,255,255,0.35)',
  text: '#d7dae0',
  muted: '#8b929c',
  font: 'Inter, system-ui, sans-serif',
  mono: '"JetBrains Mono", ui-monospace, monospace',
};

export type Prim =
  | { t: 'rect'; x: number; y: number; w: number; h: number; fill: string }
  | { t: 'path'; pts: number[]; stroke: string; width: number; dash?: number[]; clip?: boolean }
  | { t: 'area'; pts: number[]; fill: string; clip?: boolean }
  | { t: 'circle'; x: number; y: number; r: number; fill: string; stroke?: string; clip?: boolean }
  | {
      t: 'text';
      x: number;
      y: number;
      text: string;
      fill: string;
      size: number;
      align: 'left' | 'center' | 'right';
      baseline: 'top' | 'middle' | 'bottom';
      weight?: number;
      rotate?: boolean;
      mono?: boolean;
    };

export interface Scene {
  width: number;
  height: number;
  prims: Prim[];
  plot: { x: number; y: number; w: number; h: number };
  xRange: [number, number];
  yRange: [number, number];
  y2Range: [number, number] | null;
  yLog: boolean;
  theme: PlotTheme;
  /** Data -> pixel mappers. */
  sx: (x: number) => number;
  sy: (y: number) => number;
  invX: (px: number) => number;
}

/** "Nice" tick values covering [lo, hi]. */
export function niceTicks(lo: number, hi: number, count: number): number[] {
  if (!(hi > lo)) return [lo];
  const raw = (hi - lo) / Math.max(1, count);
  const p = Math.pow(10, Math.floor(Math.log10(raw)));
  const m = raw / p;
  const step = (m >= 5 ? 10 : m >= 2 ? 5 : m >= 1.5 ? 2 : 1) * p;
  const out: number[] = [];
  for (let v = Math.ceil(lo / step - 1e-9) * step; v <= hi + step * 1e-9; v += step) out.push(Math.abs(v) < step * 1e-9 ? 0 : v);
  return out;
}

function logTicks(lo: number, hi: number): number[] {
  const out: number[] = [];
  for (let e = Math.floor(Math.log10(lo)); e <= Math.ceil(Math.log10(hi)); e++)
    for (const m of [1, 2, 5]) {
      const v = m * Math.pow(10, e);
      if (v >= lo && v <= hi) out.push(v);
    }
  return out;
}

function extent(values: ArrayLike<number>[], xs: ArrayLike<number>[], xr: [number, number] | null, positive: boolean): [number, number] {
  let lo = Infinity;
  let hi = -Infinity;
  values.forEach((ys, k) => {
    const x = xs[k];
    for (let i = 0; i < ys.length; i++) {
      const v = ys[i];
      if (!Number.isFinite(v) || (positive && v <= 0)) continue;
      if (xr && (x[i] < xr[0] || x[i] > xr[1])) continue;
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
  });
  if (!Number.isFinite(lo)) return positive ? [1, 10] : [0, 1];
  if (hi === lo) {
    const d = Math.abs(lo) * 0.05 || 1;
    return positive ? [lo / 2, hi * 2] : [lo - d, hi + d];
  }
  if (positive) return [lo / 1.15, hi * 1.15];
  const pad = (hi - lo) * 0.06;
  return [lo - pad, hi + pad];
}

/** Min/max decimation per pixel column for very long series. */
function decimate(
  x: ArrayLike<number>,
  y: ArrayLike<number>,
  sx: (v: number) => number,
  sy: (v: number) => number,
  x0: number,
  x1: number,
): number[][] {
  const runs: number[][] = [];
  let cur: number[] = [];
  const n = y.length;
  const dense = n > (x1 - x0) * 3;
  if (!dense) {
    for (let i = 0; i < n; i++) {
      if (!Number.isFinite(y[i]) || !Number.isFinite(x[i])) {
        if (cur.length) runs.push(cur);
        cur = [];
        continue;
      }
      cur.push(sx(x[i]), sy(y[i]));
    }
    if (cur.length) runs.push(cur);
    return runs;
  }
  let col = -Infinity;
  let mn = 0;
  let mx = 0;
  let has = false;
  const flush = () => {
    if (!has) return;
    cur.push(col, sy(mn), col, sy(mx));
    has = false;
  };
  for (let i = 0; i < n; i++) {
    const v = y[i];
    if (!Number.isFinite(v)) {
      flush();
      if (cur.length) runs.push(cur);
      cur = [];
      continue;
    }
    const c = Math.round(sx(x[i]));
    if (c !== col) {
      flush();
      col = c;
      mn = mx = v;
      has = true;
    } else {
      if (v < mn) mn = v;
      if (v > mx) mx = v;
    }
  }
  flush();
  if (cur.length) runs.push(cur);
  return runs;
}

export function buildScene(spec: PlotSpec, width: number, height: number): Scene {
  const theme = spec.theme ?? DARK_THEME;
  const prims: Prim[] = [];
  const y1s = spec.series.filter((s) => s.axis !== 'y2');
  const y2s = spec.series.filter((s) => s.axis === 'y2');
  const hasLegend = spec.legend !== false && spec.series.length > 1;
  const top = (spec.title ? 22 : 8) + (hasLegend ? 18 : 0);
  const left = 66;
  const right = y2s.length ? 64 : 16;
  const bottom = 40;
  const plot = { x: left, y: top, w: Math.max(10, width - left - right), h: Math.max(10, height - top - bottom) };

  // X range
  let xr: [number, number];
  if (spec.xRange) xr = spec.xRange;
  else {
    let lo = Infinity;
    let hi = -Infinity;
    for (const s of spec.series)
      for (let i = 0; i < s.x.length; i++) {
        const v = s.x[i];
        if (Number.isFinite(v) && Number.isFinite(s.y[i])) {
          if (v < lo) lo = v;
          if (v > hi) hi = v;
        }
      }
    xr = Number.isFinite(lo) ? (hi > lo ? [lo, hi] : [lo - 1, hi + 1]) : [0, 1];
  }
  const yLog = !!spec.yLog;
  const bandVals = (spec.bands ?? []).flatMap((b) => [b.lo, b.hi]);
  const bandXs = (spec.bands ?? []).flatMap((b) => [b.x, b.x]);
  let yr: [number, number] = spec.yRange ?? extent([...y1s.map((s) => s.y), ...bandVals], [...y1s.map((s) => s.x), ...bandXs], xr, yLog);
  if (!spec.yRange && !yLog && spec.hlines?.length) {
    for (const h of spec.hlines)
      if (Number.isFinite(h.y) && h.y >= yr[0] - (yr[1] - yr[0]) && h.y <= yr[1] + (yr[1] - yr[0])) {
        yr = [Math.min(yr[0], h.y), Math.max(yr[1], h.y)];
      }
  }
  const y2r: [number, number] | null = y2s.length
    ? extent(
        y2s.map((s) => s.y),
        y2s.map((s) => s.x),
        xr,
        false,
      )
    : null;

  const sx = (x: number) => plot.x + ((x - xr[0]) / (xr[1] - xr[0])) * plot.w;
  const invX = (px: number) => xr[0] + ((px - plot.x) / plot.w) * (xr[1] - xr[0]);
  const sy = yLog
    ? (y: number) =>
        plot.y + plot.h - ((Math.log10(Math.max(y, 1e-300)) - Math.log10(yr[0])) / (Math.log10(yr[1]) - Math.log10(yr[0]))) * plot.h
    : (y: number) => plot.y + plot.h - ((y - yr[0]) / (yr[1] - yr[0])) * plot.h;
  const sy2 = y2r ? (y: number) => plot.y + plot.h - ((y - y2r[0]) / (y2r[1] - y2r[0])) * plot.h : sy;

  prims.push({ t: 'rect', x: 0, y: 0, w: width, h: height, fill: theme.bg });

  // Grid and ticks
  const xt = niceTicks(xr[0], xr[1], Math.max(2, Math.floor(plot.w / 90)));
  const yt = yLog ? logTicks(yr[0], yr[1]) : niceTicks(yr[0], yr[1], Math.max(2, Math.floor(plot.h / 45)));
  for (const v of xt) {
    const x = sx(v);
    prims.push({ t: 'path', pts: [x, plot.y, x, plot.y + plot.h], stroke: theme.grid, width: 1 });
    prims.push({
      t: 'text',
      x,
      y: plot.y + plot.h + 6,
      text: fmt(v, 5),
      fill: theme.muted,
      size: 10.5,
      align: 'center',
      baseline: 'top',
      mono: true,
    });
  }
  for (const v of yt) {
    const y = sy(v);
    prims.push({ t: 'path', pts: [plot.x, y, plot.x + plot.w, y], stroke: theme.grid, width: 1 });
    prims.push({
      t: 'text',
      x: plot.x - 6,
      y,
      text: fmt(v, 4),
      fill: theme.muted,
      size: 10.5,
      align: 'right',
      baseline: 'middle',
      mono: true,
    });
  }
  if (y2r) {
    for (const v of niceTicks(y2r[0], y2r[1], Math.max(2, Math.floor(plot.h / 45)))) {
      prims.push({
        t: 'text',
        x: plot.x + plot.w + 6,
        y: sy2(v),
        text: fmt(v, 4),
        fill: theme.muted,
        size: 10.5,
        align: 'left',
        baseline: 'middle',
        mono: true,
      });
    }
  }
  prims.push({ t: 'path', pts: [plot.x, plot.y, plot.x, plot.y + plot.h, plot.x + plot.w, plot.y + plot.h], stroke: theme.axis, width: 1 });
  prims.push({
    t: 'text',
    x: plot.x + plot.w / 2,
    y: height - 4,
    text: spec.xLabel,
    fill: theme.text,
    size: 11.5,
    align: 'center',
    baseline: 'bottom',
    weight: 500,
  });
  prims.push({
    t: 'text',
    x: 14,
    y: plot.y + plot.h / 2,
    text: spec.yLabel,
    fill: theme.text,
    size: 11.5,
    align: 'center',
    baseline: 'middle',
    weight: 500,
    rotate: true,
  });
  if (y2r && spec.y2Label)
    prims.push({
      t: 'text',
      x: width - 10,
      y: plot.y + plot.h / 2,
      text: spec.y2Label,
      fill: theme.text,
      size: 11.5,
      align: 'center',
      baseline: 'middle',
      weight: 500,
      rotate: true,
    });
  if (spec.title)
    prims.push({ t: 'text', x: plot.x, y: 6, text: spec.title, fill: theme.text, size: 12.5, align: 'left', baseline: 'top', weight: 600 });

  // Shaded spans (e.g. clipped regions)
  for (const s of spec.spans ?? []) {
    const a = Math.max(plot.x, sx(s.x0));
    const b = Math.min(plot.x + plot.w, sx(s.x1));
    if (b > a) prims.push({ t: 'rect', x: a, y: plot.y, w: b - a, h: plot.h, fill: s.color });
  }

  // Envelopes
  for (const b of spec.bands ?? []) {
    let run: number[] = [];
    let back: number[] = [];
    const flush = () => {
      if (run.length >= 4) prims.push({ t: 'area', pts: [...run, ...back], fill: b.color, clip: true });
      run = [];
      back = [];
    };
    for (let i = 0; i < b.x.length; i++) {
      if (!Number.isFinite(b.lo[i]) || !Number.isFinite(b.hi[i])) {
        flush();
        continue;
      }
      run.push(sx(b.x[i]), sy(b.hi[i]));
      back.unshift(sx(b.x[i]), sy(b.lo[i]));
    }
    flush();
  }

  for (const h of spec.hlines ?? []) {
    if (!Number.isFinite(h.y)) continue;
    const y = sy(h.y);
    prims.push({ t: 'path', pts: [plot.x, y, plot.x + plot.w, y], stroke: h.color, width: 1, dash: h.dash ?? [4, 4], clip: true });
    if (h.label)
      prims.push({
        t: 'text',
        x: plot.x + plot.w - 4,
        y: y - 3,
        text: h.label,
        fill: h.color,
        size: 10,
        align: 'right',
        baseline: 'bottom',
      });
  }
  for (const v of spec.vlines ?? []) {
    if (!Number.isFinite(v.x)) continue;
    const x = sx(v.x);
    prims.push({ t: 'path', pts: [x, plot.y, x, plot.y + plot.h], stroke: v.color, width: 1, dash: v.dash, clip: true });
    if (v.label) prims.push({ t: 'text', x: x + 3, y: plot.y + 2, text: v.label, fill: v.color, size: 10, align: 'left', baseline: 'top' });
  }

  for (const s of spec.series) {
    const syy = s.axis === 'y2' ? sy2 : sy;
    for (const run of decimate(s.x, s.y, sx, syy, plot.x, plot.x + plot.w)) {
      if (run.length === 2) prims.push({ t: 'circle', x: run[0], y: run[1], r: 1.5, fill: s.color, clip: true });
      else prims.push({ t: 'path', pts: run, stroke: s.color, width: s.width ?? 1.5, dash: s.dash, clip: true });
    }
    if (s.dots && s.y.length < plot.w / 3)
      for (let i = 0; i < s.y.length; i++)
        if (Number.isFinite(s.y[i])) prims.push({ t: 'circle', x: sx(s.x[i]), y: syy(s.y[i]), r: 2, fill: s.color, clip: true });
  }

  for (const w of spec.widths ?? []) {
    const y = sy(w.y);
    const a = sx(w.x0);
    const b = sx(w.x1);
    prims.push({ t: 'path', pts: [a, y, b, y], stroke: w.color, width: 1.5, clip: true });
    prims.push({ t: 'path', pts: [a, y - 5, a, y + 5], stroke: w.color, width: 1.5, clip: true });
    prims.push({ t: 'path', pts: [b, y - 5, b, y + 5], stroke: w.color, width: 1.5, clip: true });
    if (w.label)
      prims.push({
        t: 'text',
        x: (a + b) / 2,
        y: y - 4,
        text: w.label,
        fill: w.color,
        size: 10,
        align: 'center',
        baseline: 'bottom',
        weight: 600,
      });
  }
  for (const p of spec.points ?? []) {
    prims.push({ t: 'circle', x: sx(p.x), y: sy(p.y), r: 4, fill: p.color, stroke: '#000', clip: true });
    if (p.label)
      prims.push({
        t: 'text',
        x: sx(p.x) + 6,
        y: sy(p.y) - 6,
        text: p.label,
        fill: p.color,
        size: 10,
        align: 'left',
        baseline: 'bottom',
        weight: 600,
      });
  }

  if (hasLegend) {
    let x = plot.x;
    const y = (spec.title ? 22 : 8) + 8;
    for (const s of spec.series) {
      prims.push({ t: 'path', pts: [x, y, x + 16, y], stroke: s.color, width: 2.5, dash: s.dash });
      prims.push({ t: 'text', x: x + 20, y, text: s.label, fill: theme.text, size: 10.5, align: 'left', baseline: 'middle' });
      x += 32 + s.label.length * 6.2;
      if (x > width - 60) break;
    }
  }

  return { width, height, prims, plot, xRange: xr, yRange: yr, y2Range: y2r, yLog, theme, sx, sy, invX };
}
